import React, { useEffect, useRef, useState, useCallback } from "react";
import "./dino.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import Task from "../Task";
import { useAudio } from "../../../Providers/AudioProvider";
import logo from "../../../assets/images/coinlogo.png";
import { userGameRewards } from "../../../apis/user";
import { logPageView } from "../../../components/analytics";

const DinoGame = () => {
  const gameContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const { userDetails, updateUserInfo, watchScreen, updatewatchScreenInfo } =
    useUserInfo();
  const playClick = useAudio();
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit";

  // Memoize the showGameOverPopup function to prevent recreating it on every render
  const showGameOverPopup = useCallback((finalScore) => {
    // Update the score state
    setScore(finalScore);

    // Update high score if needed
    setHighScore((prevHighScore) => Math.max(prevHighScore, finalScore));

    // Ensure the iframe is not interactive
    const iframe = document.getElementById("dino-game-iframe");
    if (iframe) {
      iframe.style.pointerEvents = "none";
    }

    // Call API to record score - popup will be shown after API response
    handleApiResponse(finalScore);
  }, []);

  useEffect(() => {
    logPageView("Dino ", "Dino game");
  }, []);

  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: Task,
      currentComponentText: "task",
      lastComponent: userDetails?.currentComponent,
      lastComponentText: userDetails?.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.menuCount + 1,
    }));
  };

  const handleApiResponse = async (points) => {
    if (points <= 0) {
      return;
    }
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setIsLoading(true);
    try {
      const apiData = {
        gamePoints: points,
        gameType: "dino",
      };
      const res = await userGameRewards(apiData);
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        setIsLoading(false);

        // Only show game over popup after API response is received
        setGameOver(true);
      }
    } catch (error) {
      console.error("API call failed:", error);
      setIsLoading(false);

      // Still show game over popup even if API fails
      setGameOver(true);
    }
  };

  const closePopUp = () => {
    // Guard against multiple calls
    if (isLoading) return;

    // Set loading state to prevent multiple clicks
    setIsLoading(true);

    try {
      // Find the iframe by ID for more reliable reference
      const iframe = document.getElementById("dino-game-iframe");

      if (iframe) {
        // Re-enable pointer events on the iframe
        iframe.style.pointerEvents = "auto";

        // Reset the iframe by refreshing its src
        const currentSrc = iframe.src;
        iframe.src = "about:blank"; // Use about:blank to ensure clean slate

        // Slight delay to ensure src is cleared before setting again
        setTimeout(() => {
          iframe.src = currentSrc;

          // Add a listener to know when the iframe has reloaded
          iframe.onload = () => {
            // After iframe is fully reloaded, clear game over state and loading
            setGameOver(false);
            setIsLoading(false);
            setScore(0);
          };
        }, 150); // Slightly longer delay for better reliability
      } else {
        // If iframe not found by ID, recreate the game completely
        setupGame();

        // Reset states after a short delay
        setTimeout(() => {
          setGameOver(false);
          setIsLoading(false);
          setScore(0);
        }, 200);
      }
    } catch (error) {
      console.error("Error resetting game:", error);
      // Reset states even if there's an error
      setGameOver(false);
      setIsLoading(false);
      setScore(0);
    }
  };

  // Extract setupGame to a separate function so it can be reused
  const setupGame = useCallback(() => {
    // First, make sure container exists
    if (!gameContainerRef.current) return;

    // Clear previous content completely
    gameContainerRef.current.innerHTML = "";

    // Create a fresh iframe
    const gameFrame = document.createElement("iframe");
    gameFrame.id = "dino-game-iframe";
    gameFrame.src = "/dinogame/index.html";
    gameFrame.title = "Dino Game";
    gameFrame.className = "dino-game-iframe";
    gameFrame.frameBorder = "0";
    gameFrame.allow =
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope";
    gameFrame.allowFullScreen = true;

    // Store reference to iframe
    iframeRef.current = gameFrame;

    // Append the iframe to container
    gameContainerRef.current.appendChild(gameFrame);

    // When the iframe loads, inject script to send messages from the game
    gameFrame.onload = () => {
      try {
        const dinoScript = document.createElement("script");
        dinoScript.innerHTML = `
          // Ensure direct access to Runner instance
          let gameRunner = null;
          
          // Observer to detect when the Runner is available
          const gameObserver = new MutationObserver(function(mutations) {
            if (window.Runner && !gameRunner) {
              console.log("Dino game Runner detected, setting up hooks");
              gameRunner = window.Runner.instance_;
              setupGameHooks();
              gameObserver.disconnect();
            }
          });
          
          // Start observing for Runner
          gameObserver.observe(document.body, { childList: true, subtree: true });
          
          // Setup all game hooks
          function setupGameHooks() {
            if (!window.Runner || !Runner.prototype) {
              console.error("Could not find Runner prototype");
              return;
            }
            
            // Helper function to convert raw distance to displayed score
            function getActualScore(distance) {
              return distance ? Math.round(distance * 0.01) : 0;
            }
            
            // Save original update method
            const originalUpdate = Runner.prototype.update;
            
            // Override update to send score updates
            Runner.prototype.update = function() {
              // Call original update
              const result = originalUpdate.apply(this, arguments);
              
              // Send score update (throttled)
              if (this.distanceRan && this.frameCount % 5 === 0) {
                // Get the actual displayed score
                const actualScore = getActualScore(this.distanceRan);
                const actualHighScore = getActualScore(this.highestScore);
                
                // Send score update to parent
                window.parent.postMessage({
                  type: "SCORE_UPDATE",
                  data: {
                    score: actualScore,
                    highScore: actualHighScore,
                    displayedScore: this.distanceMeter?.digits ? parseInt(this.distanceMeter.digits.join('') || '0') : actualScore,
                    displayedHighScore: this.distanceMeter?.highScore ? parseInt(this.distanceMeter.highScore.slice(3)?.join('') || '0') : actualHighScore
                  }
                }, "*");
              }
              
              return result;
            };
            
            // Save original gameOver method
            const originalGameOver = Runner.prototype.gameOver;
            
            // Override gameOver to send game over message
            Runner.prototype.gameOver = function() {
              console.log("GAME OVER DETECTED");
              
              // Get score before calling original (which might reset things)
              const finalScore = getActualScore(this.distanceRan);
              const displayedScore = this.distanceMeter?.digits ? parseInt(this.distanceMeter.digits.join('') || '0') : finalScore;
              
              // Call original gameOver
              originalGameOver.apply(this, arguments);
              
              // Prepare game over data
              const gameOverData = {
                score: finalScore,
                displayedScore: displayedScore
              };
              
              console.log("Sending game over with score:", gameOverData);
              
              // Send immediate game over message
              window.parent.postMessage({
                type: "GAME_OVER",
                data: gameOverData
              }, "*");
              
              // Disable all controls immediately
              if (this.containerEl) {
                this.containerEl.style.pointerEvents = 'none';
              }
            };
            
            // Override restart to notify parent
            const originalRestart = Runner.prototype.restart;
            Runner.prototype.restart = function() {
              // Notify parent before restart
              window.parent.postMessage({
                type: "GAME_RESTART"
              }, "*");
              
              // Call original restart
              return originalRestart.apply(this, arguments);
            };
            
            if (window.DistanceMeter && DistanceMeter.prototype) {
              // Override the drawHighScore method to prevent showing the high score
              DistanceMeter.prototype.drawHighScore = function() {
                // Do nothing - this prevents the high score from being drawn
              };
              
              // Also modify the setHighScore to not create the high score array
              DistanceMeter.prototype.setHighScore = function(distance) {
                // We still store the high score but don't display it
                this.highestScore = this.getActualDistance(distance);
                
                // Don't create the high score array that shows "HI" prefix
                this.highScore = [];
                return;
              };
            }
            
            console.log("All Dino game hooks successfully installed");
          }
          
          // Try to set up immediately if Runner already exists
          if (window.Runner && Runner.instance_) {
            gameRunner = Runner.instance_;
            setupGameHooks();
          }
        `;

        // Add script to the iframe document
        gameFrame.contentDocument.body.appendChild(dinoScript);

        console.log("Dino game script injected");
      } catch (error) {
        console.error("Failed to inject script into Dino game:", error);
      }
    };
  }, []);

  useEffect(() => {
    // Setup the game initially
    setupGame();

    // Handler for messages from the game iframe
    const handleMessage = (event) => {
      // Check if the message is from our game
      const { type, data } = event.data || {};

      if (!type) return; // Skip if no type

      if (type === "SCORE_UPDATE" && data) {
        // Update current score
        setScore(data.displayedScore || data.score || 0);

        // Update high score if current score is higher
        setHighScore((prevHighScore) => {
          const currentScore = data.displayedScore || data.score || 0;
          return Math.max(prevHighScore, currentScore);
        });
      } else if (
        (type === "GAME_OVER" || type === "GAME_OVER_CONFIRM") &&
        data
      ) {
        // Skip if already in game over state
        if (gameOver) {
          console.log("Already in game over state, skipping duplicate message");
          return;
        }

        console.log("Processing game over with data:", data);

        // Get the final score
        const finalScore = data.displayedScore || data.score || 0;

        // Directly call the popup function
        showGameOverPopup(finalScore);
      } else if (type === "GAME_RESTART") {
        // Game was restarted
        console.log("Game restarted");

        // Reset game over state if it was set
        if (gameOver) {
          setGameOver(false);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Cleanup function
    return () => {
      window.removeEventListener("message", handleMessage);
      if (gameContainerRef.current) {
        gameContainerRef.current.innerHTML = "";
      }
    };
  }, [setupGame, gameOver, showGameOverPopup]);

  return (
    <div className="dino-game-container">
      {/* Title section */}
      <h1 className="welcometextmeme dino-game-title">DINO GAME</h1>

      {/* Game container - Single iframe will be injected here by useEffect */}
      <div className="game-wrapper-dino">
        <div
          ref={gameContainerRef}
          id="dino-game-container"
          className="dino-game-frame-container"
        ></div>
        {gameOver && (
          <div
            className="game-interaction-blocker"
            onClick={(e) => e.preventDefault()}
          ></div>
        )}
      </div>

      {/* Score section */}
      <div className="score-container-dino">
        <img
          onClick={() => {
            if (!isLoading) toogleMenu();
          }}
          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
          className="cancel-imgpoints-dino"
          style={{ cursor: "pointer", width: "25px" }}
          alt="Back"
        />
        <div className="score-display">
          <div id="currentScore">SCORE: {score}</div>
          <div id="highScore">HIGH SCORE: {highScore}</div>
        </div>
      </div>

      {/* Instructions section */}
      <div className="dino-game-instructions">
        <p>MAKE THE DINO RUN AND GET $MEMETV TOKENS</p>
      </div>

      {/* Game Over Popup */}
      {gameOver && (
        <div className="dino-overlay">
          <div className="dino-full-popup">
            <div className="dino-popup-content">
              <h2 className="dino-epic">GAME OVER!</h2>
              <img
                src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                className="dino-cancel-img"
                onClick={closePopUp}
                alt="Close"
              />
              {!IsPointsReached ? (
                <div className="row text-center">
                  <div className="col-12">
                    <div
                      className="dino-epic-div"
                      style={{ padding: "30px 40px" }}
                    >
                      <p className="dino-pointsLabel">TOKENS EARNED</p>
                      <div className="dino-pointsValue1">
                        <img src={logo} alt="Coin logo" />
                        <p className="dino-pointsNumber">{score}</p>
                      </div>
                    </div>
                    <p className="dino-bottom-text">
                      Keep jumping! 🦖 Rack up tokens and chase the high score!
                    </p>
                  </div>
                </div>
              ) : (
                <div className="row text-center">
                  <div className="col-12">
                    <div className="dino-epic-div">
                      <p
                        className="dino-pointsLabel"
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          margin: "20px 0",
                          textAlign: "center",
                        }}
                      >
                        POINTS EARNED: {score} <br />
                        TOKENS EARNED: NIL
                      </p>
                    </div>
                    <p className="dino-bottom-text">
                      Keep jumping! 🦖 Rack up tokens and chase the high score!
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DinoGame;
